import React, { ReactNode, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import Icon from "components/icon";
import Input from "components/input";
import DateRangePicker from "components/date-range-picker";
import Typography from "components/typography";
import { useAppTranslation } from "locale/useAppTranslation";
import HistoryDataSidebarTimelineItem from "./history-data-sidebar-timeline-item";

export interface FormattedFieldChange {
  fieldLabel: string;
  oldValue?: string;
  newValue?: string;
  type?: "change" | "add" | "delete";
  sentence?: string;
}

export interface HistoryDataSidebarSummaryItem {
  id: string;
  label: string;
  value: ReactNode;
}

export interface HistoryDataSidebarItem {
  id: string | number;
  timestampLabel: string;
  rawTimestamp?: string;
  actorName: string;
  actionType?: "CREATE" | "UPDATE" | "STATUS_UPDATED" | "DELETE" | "IMPORT" | string;
  actionLabel?: string;
  fieldChanges?: FormattedFieldChange[];
  reason?: string;
  description?: ReactNode;
  detail?: ReactNode;
}

interface HistoryDataSidebarProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  subtitle?: string;
  summaryItems?: HistoryDataSidebarSummaryItem[];
  items: HistoryDataSidebarItem[];
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  fromDateValue?: string;
  toDateValue?: string;
  dateValue?: string; // Backward compatibility
  onDateChange?: (from: string, to: string) => void;
  datePlaceholder?: string;
  emptyTitle?: string;
  emptyDescription?: string;
  widthClassName?: string;
  testIdPrefix?: string;
  /** Infinite scroll. Render nothing extra unless onLoadMore is set. */
  hasMore?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
}

const HistoryDataSidebar: React.FC<HistoryDataSidebarProps> = ({
  open,
  onClose,
  title,
  subtitle,
  summaryItems = [],
  items,
  searchValue = "",
  onSearchChange,
  searchPlaceholder,
  fromDateValue = "",
  toDateValue = "",
  dateValue = "", // Backward compatibility
  onDateChange,
  datePlaceholder,
  emptyTitle,
  emptyDescription,
  widthClassName = "max-w-[380px] md:max-w-[420px]",
  testIdPrefix = "history-data-sidebar",
  hasMore = false,
  isLoadingMore = false,
  onLoadMore,
}) => {
  const { t } = useAppTranslation("historyDataSidebar");

  // Determine effective from/to values for backward compatibility
  const effectiveFrom = fromDateValue || dateValue;
  const effectiveTo = toDateValue || (dateValue && !fromDateValue ? dateValue : "");

  useEffect(() => {
    if (!open) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose, open]);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  if (typeof document === "undefined") {
    return null;
  }

  const resolvedTitle = title ?? t("title");
  const resolvedSubtitle = subtitle ?? t("subtitle");

  // Infinite scroll: observe the sentinel at the end of the list. When it
  // enters the viewport (with pre-load margin), ask for the next page.
  // The observer re-attaches when the list grows, so a still-visible
  // sentinel keeps pulling pages until the viewport is full or the
  // server runs out.
  const scrollRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    const root = scrollRef.current;
    if (!sentinel || !root || !onLoadMore || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          onLoadMore();
        }
      },
      { root, rootMargin: "160px", threshold: 0 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [onLoadMore, hasMore, items.length]);

  return ReactDOM.createPortal(
    <>
      <div
        className={`fixed inset-0 z-[1000] bg-black/30 transition-opacity duration-300 ${
          open ? "opacity-100" : "pointer-events-none opacity-0"
        }`}
        onClick={onClose}
        aria-hidden="true"
        data-testid={`${testIdPrefix}-backdrop`}
      />

      <aside
        className={`fixed right-0 top-0 z-[1001] flex h-screen w-full ${widthClassName} transform flex-col overflow-hidden rounded-l-[24px] bg-white shadow-[0_24px_80px_rgba(15,23,42,0.18)] transition-transform duration-300 ease-out ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
        aria-hidden={!open}
        aria-label={resolvedTitle}
        data-testid={testIdPrefix}
      >
        <div className="border-b border-border px-8 pb-6 pt-8">
          <div className="flex items-start justify-between gap-4">
            <div>
              <Typography variant="heading4" as="h2">
                {resolvedTitle}
              </Typography>
              <div className="mt-2 flex items-start gap-2">
                <Icon name="information" size={16} className="mt-0.5 text-text-tertiary" />
                <Typography variant="bodyMedium" tone="muted">
                  {resolvedSubtitle}
                </Typography>
              </div>
            </div>

            <button
              type="button"
              className="rounded-full p-2 text-text-secondary transition-colors hover:bg-greyScale-90"
              onClick={onClose}
              aria-label={t("close")}
              data-testid={`${testIdPrefix}-close`}
            >
              <Icon name="close-2" size={18} />
            </button>
          </div>
        </div>

        {summaryItems.length > 0 && (
          <div className="border-b border-border px-8 py-6">
            <div className="rounded-2xl bg-greyScale-95 px-6 py-5">
              <div className="grid gap-4">
                {summaryItems.map((item) => (
                  <div key={item.id} className="grid grid-cols-[120px_1fr] gap-4">
                    <Typography variant="body" tone="secondary">
                      {item.label}
                    </Typography>
                    <div>
                      {typeof item.value === "string" ? (
                        <Typography variant="body" weight="medium">
                          {item.value}
                        </Typography>
                      ) : (
                        item.value
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {(onSearchChange || onDateChange) && (
          <div className="border-b border-border px-8 py-6">
            <div className="grid gap-3 md:grid-cols-2">
              {onSearchChange && (
                <Input
                  value={searchValue}
                  onChange={(event) => onSearchChange(event.target.value)}
                  leftIcon={<Icon name="search" size={16} />}
                  placeholder={searchPlaceholder ?? t("search-placeholder")}
                  aria-label={t("search")}
                  reserveHelperSpace={false}
                  parentClassName="gap-0"
                />
              )}

              {onDateChange && (
                <DateRangePicker
                  fromValue={effectiveFrom}
                  toValue={effectiveTo}
                  onChange={(from, to) => {
                    (onDateChange as any)(from, to);
                  }}
                  placeholder={datePlaceholder ?? t("date-placeholder")}
                  reserveHelperSpace={false}
                  parentClassName="gap-0"
                />
              )}
            </div>
          </div>
        )}

        <div ref={scrollRef} className="flex-1 overflow-y-auto px-8 py-6">
          {items.length === 0 ? (
            <div className="flex min-h-full items-center justify-center text-center">
              <div className="max-w-[220px]">
                <Typography variant="heading6">
                  {emptyTitle ?? t("empty-title")}
                </Typography>
                <Typography variant="body" tone="muted" className="mt-2">
                  {emptyDescription ?? t("empty-description")}
                </Typography>
              </div>
            </div>
          ) : (
            <>
              {items.map((item, index) => (
                <HistoryDataSidebarTimelineItem
                  key={item.id}
                  item={item}
                  isLast={index === items.length - 1}
                />
              ))}
              {onLoadMore && (isLoadingMore || hasMore) && (
                <div
                  ref={sentinelRef}
                  className="flex justify-center py-4"
                  data-testid={`${testIdPrefix}-load-more-sentinel`}
                >
                  {isLoadingMore && (
                    <Icon
                      name="refresh"
                      size={18}
                      className="animate-spin text-text-tertiary"
                    />
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </aside>
    </>,
    document.body,
  );
};

export default HistoryDataSidebar;